import React, { useEffect, useState } from 'react';
import { FaUsers, FaSpinner, FaStar, FaShieldAlt } from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';
import { API_URL } from '../config';
import CompanyUsersCard from '../components/CompanyUsersCard';

// ============================================
// MY TEAM (client portal)
// The company's own logins, in one place.
//   - EVERYONE at the company can SEE who has access.
//   - Only the company's PRIMARY CONTACT can add / remove / promote. The backend
//     enforces exactly the same rule (routes/company-users.js), so the manage card is
//     simply not shown to anyone else.
// ============================================

function ClientTeam({ onLogout }) {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [loading, setLoading] = useState(true);
  const [people, setPeople] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [client, setClient] = useState(null);

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // /client/team            = our own people + the One Purpose admins we can message
        // /clients?user_id=       = the company record, used to confirm that WE are the
        //                           primary contact (the same check My Profile uses)
        const [t, c] = await Promise.all([
          fetch(`${API_URL}/client/team`, { headers: auth() }).then((r) => r.json()).catch(() => null),
          fetch(`${API_URL}/clients?user_id=${user.id}`, { headers: auth() }).then((r) => r.json()).catch(() => null)
        ]);
        if (cancelled) return;
        if (t && t.success) { setPeople(t.company_people || []); setAdmins(t.admins || []); }
        if (c && c.success && Array.isArray(c.clients) && c.clients[0]) setClient(c.clients[0]);
      } catch (e) { /* the explainer below still renders */ }
      finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirror the backend rule EXACTLY (routes/company-users.js -> canManage): you may
  // manage the company if you are clients.user_id, OR your company_users row is
  // flagged is_primary. Anything else is refused with a 403, so the card stays hidden.
  const me = people.find((p) => p.is_me);
  const isPrimary = !!(me && (me.is_primary || (client && client.user_id && client.user_id === user.id)));
  const primaryPerson = people.find((p) => p.is_primary);

  return (
    <ClientLayout activeTab="team" onLogout={onLogout} user={user}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#0A1929' }}>
          <FaUsers style={{ color: '#00A86B' }} /> My Team
        </h1>
        <p className="text-sm mt-1" style={{ color: '#4A5568' }}>
          Everyone who can sign in to {client?.company_name || 'your company'}. Each person gets their own login — please never share one.
        </p>
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-400">
          <FaSpinner className="animate-spin inline" size={24} />
        </div>
      ) : (
        <>
          {/* Read-only overview — visible to everyone at the company. */}
          <div className="bg-white rounded-xl border p-6 mb-6" style={{ borderColor: '#E8ECEF' }}>
            <h2 className="font-semibold text-navy mb-4 flex items-center gap-2">
              <FaUsers size={14} style={{ color: '#00A86B' }} /> Company logins
            </h2>
            <div className="space-y-2">
              {people.length === 0 && (
                <p className="text-sm text-gray-400">No logins yet.</p>
              )}
              {people.map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-navy truncate">
                      {p.full_name}
                      {p.is_primary && (
                        <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green/10 text-green inline-flex items-center gap-1">
                          <FaStar size={8} /> Primary contact
                        </span>
                      )}
                      {p.is_me && (
                        <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600">You</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-400 truncate">
                      {p.email}{p.role_label ? ` · ${p.role_label}` : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* The manage card — primary contact only (the backend enforces the same rule). */}
          {isPrimary && client && (
            <CompanyUsersCard
              companyType="client"
              companyId={client.id}
              companyName={client.company_name}
              className="rounded-xl border p-6"
            />
          )}

          {!isPrimary && (
            <div className="rounded-xl border p-4 mb-6 flex items-start gap-3" style={{ borderColor: '#E8ECEF', backgroundColor: '#ffffff' }}>
              <FaShieldAlt className="mt-0.5" style={{ color: '#94A3B8' }} size={14} />
              <p className="text-sm" style={{ color: '#4A5568' }}>
                {primaryPerson
                  ? <>Only your company&apos;s primary contact ({primaryPerson.full_name}) can add or remove logins. Ask them to set you up.</>
                  : <>No primary contact has been set for your company yet, so logins cannot be managed here. Please ask One Purpose Logistics to set one — you will then be able to add your own team.</>}
              </p>
            </div>
          )}

          {/* One Purpose contacts — read-only, so the client always knows who to ask. */}
          {admins.length > 0 && (
            <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
              <h2 className="font-semibold text-navy mb-1">Your One Purpose Logistics contacts</h2>
              <p className="text-xs text-gray-400 mb-4">Your account team — also reachable from Messages.</p>
              <div className="flex flex-wrap gap-2">
                {admins.map((a) => (
                  <span key={a.id} className="text-xs px-3 py-1.5 rounded-full bg-gray-100 text-gray-600">
                    {a.full_name || a.email}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </ClientLayout>
  );
}

export default ClientTeam;

